import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchCandidateProfileListsQuery } from 'generated-graphql/hooks/apollo-hooks';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { CandidateProfileTalentListTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTalentListTable';
import { getCandidateProfileTalentListTableColumns } from 'modules/candidate-profile/tables/columns/getCandidateProfileTalentListTableColumns';
import { getCandidateProfileTalentListFilters } from 'modules/candidate-profile/tables/filters/getCandidateProfileTalentListFilters';
import { useCandidateListStore } from 'modules/candidate-profile/stores/useCandidateListStore';
import { useCandidateProfileTalentPoolTableActions } from 'modules/candidate-profile/tables/hooks/useCandidateProfileTalentPoolTableActions';
import { UpsertTalentPoolFormData } from 'modules/candidate-profile/forms/schema';
import {
  ListTeam,
  SearchableCandidateProfileListSortableFields,
  SearchableCandidateProfileListSortInput,
  SearchableSortDirection
} from 'generated-graphql/types';
import { TableV3Filters } from 'common/table/components/TableV3';
import { TableV3Sort } from 'common/table/types/TableV3';
import { mapCandidateProfileTalentListTableColumns } from 'modules/candidate-profile/tables/maps/mapCandidateProfileTalentListTableColumns';
import { CandidateProfileListProps } from 'modules/candidate-profile/types/CandidateProfileListProps';
import { useTeamFilterSearch } from 'modules/candidate-profile/hooks/useTeamFilterSearch';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

const DEFAULT_PAGE_SIZE = 25;

type UseCandidateProfileTalentPoolTableLogicProps = {
  candidateProfileListProps: CandidateProfileListProps;
};

export const useCandidateProfileTalentPoolTableLogic = ({
  candidateProfileListProps
}: UseCandidateProfileTalentPoolTableLogicProps) => {
  const organization = useOrganization();
  const { setTalentListsCount } = useCandidateListStore();
  const isTeamsEnabled = Boolean(useFlag(Flags.TALENT_LIST_TEAMS));
  const { searchTeams } = useTeamFilterSearch();

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<
    TableV3Filters<CandidateProfileTalentListTableColumns> | undefined
  >(undefined);
  const [sort, setSort] = useState<
    TableV3Sort<SearchableCandidateProfileListSortableFields>
  >({
    field: SearchableCandidateProfileListSortableFields.UpdatedAt,
    direction: 'desc'
  });

  const filter = getCandidateProfileTalentListFilters({ filters });
  const sortInput: SearchableCandidateProfileListSortInput[] = [
    {
      field: sort.field,
      direction:
        sort.direction === 'asc'
          ? SearchableSortDirection.Asc
          : SearchableSortDirection.Desc
    }
  ];

  const { data, loading, updateQuery } = useSearchCandidateProfileListsQuery({
    variables: {
      organizationId: organization.id,
      filter: filter && JSON.stringify(filter),
      sort: JSON.stringify(sortInput),
      limit: pageSize,
      from: currentPage * pageSize
    },
    skip: !organization.id
  });

  // Optimistically update an item in the list
  const updateItem = useCallback(
    (id: string, updates: UpsertTalentPoolFormData, teams: ListTeam[]) => {
      updateQuery(({ searchCandidateProfileListsForUser: lists }) => ({
        searchCandidateProfileListsForUser: {
          ...lists,
          items: lists.items.map((item) =>
            item.id === id
              ? {
                  ...item,
                  title: updates.name,
                  description: updates.description,
                  teams
                }
              : item
          )
        }
      }));
    },
    [updateQuery]
  );

  // Optimistically remove an item from the list
  const removeItem = useCallback(
    (id: string) => {
      updateQuery(({ searchCandidateProfileListsForUser: lists }) => ({
        searchCandidateProfileListsForUser: {
          ...lists,
          items: lists.items.filter((item) => item.id !== id),
          total: Math.max(0, (lists.total ?? lists.items.length) - 1)
        }
      }));
    },
    [updateQuery]
  );

  const { actionsRenderer } = useCandidateProfileTalentPoolTableActions({
    candidateProfileListProps,
    updateItem,
    removeItem
  });

  const candidateProfileLists = extractQueryListItems(data);

  const mappedData = mapCandidateProfileTalentListTableColumns({
    data: candidateProfileLists
  });

  // New columns remount the headers, closing an open filter after each pick,
  // so keep them stable and reach the latest handlers through refs.
  const actionsRendererRef = useRef(actionsRenderer);
  actionsRendererRef.current = actionsRenderer;
  const searchTeamsRef = useRef(searchTeams);
  searchTeamsRef.current = searchTeams;

  const columns = useMemo(
    () =>
      getCandidateProfileTalentListTableColumns({
        candidateProfileListProps,
        actionsRenderer: (data) => actionsRendererRef.current(data),
        onTeamSearch: isTeamsEnabled
          ? (query) => searchTeamsRef.current(query)
          : undefined
      }),
    [candidateProfileListProps, isTeamsEnabled]
  );

  // TableV3 rebuilds its headers when this changes, so it must stay stable.
  const handleFiltersChanged = useCallback(
    (newFilters: TableV3Filters<CandidateProfileTalentListTableColumns>) => {
      setFilters(newFilters);
      setCurrentPage(0); // Reset to first page when filters change
    },
    []
  );

  const handleSort = (
    newSort: TableV3Sort<SearchableCandidateProfileListSortableFields>
  ) => {
    setSort(newSort);
    setCurrentPage(0); // Reset to first page when sort changes
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page when page size changes
  };

  useEffect(() => {
    setTalentListsCount(data?.searchCandidateProfileListsForUser?.total ?? 0);
  }, [data?.searchCandidateProfileListsForUser?.total]);

  return {
    data: mappedData,
    columns,
    loading,
    total: data?.searchCandidateProfileListsForUser?.total ?? 0,
    sort,
    currentPage,
    pageSize,
    onFiltersChanged: handleFiltersChanged,
    onSort: handleSort,
    onChangePage: handleChangePage,
    onChangePageSize: handleChangePageSize
  };
};
